if (!customElements.get('size-guide-drawer')) {
  customElements.define(
    'size-guide-drawer',
    class SizeGuideDrawer extends HTMLElement {
      /** @type {AbortController | undefined} */
      _abort;

      /** @type {HTMLElement | null} */
      _lastFocus = null;

      connectedCallback() {
        // The drawer is authored inside `.lp-pdp__info-container`, which is
        // `position: sticky` and creates a stacking context that traps fixed
        // descendants below the site header. Re-parent to <body> on connect
        // so the drawer can paint above the header without lifting the
        // sticky PDP info column along with it.
        if (this.parentNode !== document.body && !this._reparented) {
          this._reparented = true;

          // <lih-product-tabs> fetches every tab from the same fixed
          // pdp_section_id, so this drawer's id — built from section.id — is
          // identical on every product. Switching tabs replaces the panel's
          // innerHTML, but by then the previous tab's drawer has already left
          // the panel for <body> and survives the swap. Left alone, each tab
          // visited stacks another drawer with the same id in <body>, and one
          // click opens all of them. Removing the stale one first — rather
          // than skipping our own move — keeps the most recently fetched
          // product's content current.
          if (this.id) {
            for (const other of document.body.querySelectorAll('size-guide-drawer')) {
              if (other !== this && other.id === this.id) other.remove();
            }
          }

          document.body.appendChild(this);
          return;
        }
        
        this._abort = new AbortController();
        const { signal } = this._abort;

        if (!this.hasAttribute('aria-hidden')) {
          this.setAttribute('aria-hidden', 'true');
        }

        document.addEventListener('click', this._onDocClick, { signal });
        document.addEventListener('keydown', this._onKeydown, { signal });
        this.addEventListener('click', this._onHostClick, { signal });
        this.addEventListener('click', this._onTabBarClick, { signal });
        this._initCarousels();
      }

      disconnectedCallback() {
        this._abort?.abort();
        this._abort = undefined;
        document.body.classList.remove('component-size-guide-drawer-open');
        for (const root of this.querySelectorAll('[data-js-sg-carousel]')) {
          if (root instanceof HTMLElement) {
            delete root.dataset.sgCarouselInit;
            /** @type {unknown} */
            const r = root;
            if (typeof r._sgApply === 'function') {
              delete r._sgApply;
            }
          }
        }
      }

      _onDocClick = (event) => {
        const opener = event.target?.closest?.('[data-size-guide-open]');
        if (!opener) {
          return;
        }
        const targetId = opener.getAttribute('data-size-guide-target');
        if (!targetId || targetId !== this.id) {
          return;
        }
        event.preventDefault();
        this.open(opener);
      };

      _onKeydown = (event) => {
        if (this.dataset.state !== 'open') {
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      };

      _onHostClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        if (target.closest('[data-js-size-guide-close]') || target.closest('[data-js-size-guide-overlay]')) {
          event.preventDefault();
          this.close();
        }
      };

      _onTabBarClick = (event) => {
        const btn = event.target?.closest?.('[data-js-size-guide-tab]');
        if (!(btn instanceof HTMLButtonElement) || !this.contains(btn)) {
          return;
        }
        const key = btn.getAttribute('data-tab-key');
        if (!key) {
          return;
        }
        event.preventDefault();

        for (const b of this.querySelectorAll('[data-js-size-guide-tab]')) {
          if (b instanceof HTMLButtonElement) {
            const selected = b === btn;
            b.setAttribute('aria-selected', selected ? 'true' : 'false');
            b.classList.toggle('is-active', selected);
          }
        }
        for (const panel of this.querySelectorAll('[data-js-size-guide-panel]')) {
          if (!(panel instanceof HTMLElement)) {
            continue;
          }
          const match = panel.getAttribute('data-tab-key') === key;
          panel.toggleAttribute('hidden', !match);
        }

        requestAnimationFrame(() => {
          this._reflowCarousels();
        });
      };

      _initCarousels() {
        const signal = this._abort?.signal;
        if (!signal) {
          return;
        }

        for (const root of this.querySelectorAll('[data-js-sg-carousel]')) {
          if (!(root instanceof HTMLElement)) {
            continue;
          }

          const viewport = root.querySelector('[data-js-sg-carousel-viewport]');
          const track = root.querySelector('[data-js-sg-carousel-track]');
          const slides = root.querySelectorAll('[data-js-sg-carousel-slide]');
          const prev = root.querySelector('[data-js-sg-carousel-prev]');
          const next = root.querySelector('[data-js-sg-carousel-next]');
          if (!viewport || !track || slides.length < 1) {
            continue;
          }

          const apply = () => {
            const w = viewport.clientWidth || 0;
            if (w <= 0) {
              return;
            }
            const allSlides = track.querySelectorAll('[data-js-sg-carousel-slide]');
            for (const slide of allSlides) {
              if (slide instanceof HTMLElement) {
                slide.style.flex = `0 0 ${w}px`;
                slide.style.width = `${w}px`;
              }
            }
            const idx = typeof root._sgIndex === 'number' ? root._sgIndex : 0;
            track.style.transition = 'none';
            track.style.transform = `translate3d(-${idx * w}px, 0, 0)`;
          };

          if (root.dataset.sgCarouselInit === '1') {
            /** @type {HTMLElement & { _sgApply?: () => void }} */
            const hr = root;
            hr._sgApply = apply;
            apply();
            continue;
          }
          root.dataset.sgCarouselInit = '1';
          const n = slides.length;

          // Clone slides for infinite carousel effect
          const firstSlide = slides[0];
          const lastSlide = slides[n - 1];
          
          if (firstSlide && lastSlide) {
            // Clone last slide and prepend
            const clonedLast = lastSlide.cloneNode(true);
            track.insertBefore(clonedLast, firstSlide);
            
            // Clone first slide and append
            const clonedFirst = firstSlide.cloneNode(true);
            track.appendChild(clonedFirst);
          }

          // Start at index 1 (which is the real first slide after the cloned last slide)
          root._sgIndex = 1;
          root._sgOriginalCount = n;

          const applyWithState = () => {
            const w = viewport.clientWidth || 0;
            if (w <= 0) {
              return;
            }
            const allSlides = track.querySelectorAll('[data-js-sg-carousel-slide]');
            for (const slide of allSlides) {
              if (slide instanceof HTMLElement) {
                slide.style.flex = `0 0 ${w}px`;
                slide.style.width = `${w}px`;
              }
            }
            const idx = typeof root._sgIndex === 'number' ? root._sgIndex : 1;
            track.style.transition = 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)';
            track.style.transform = `translate3d(-${idx * w}px, 0, 0)`;
          };

          /** @type {HTMLElement & { _sgApply?: () => void }} */
          const hr = root;
          hr._sgApply = applyWithState;

          if (n > 1 && prev instanceof HTMLElement && next instanceof HTMLElement) {
            prev.addEventListener(
              'click',
              (e) => {
                e.preventDefault();
                root._sgIndex = (root._sgIndex ?? 1) - 1;
                const originalCount = root._sgOriginalCount || n;
                
                applyWithState();
                
                // If we're at the cloned last slide (index 0), jump to real last slide
                setTimeout(() => {
                  if (root._sgIndex === 0) {
                    root._sgIndex = originalCount;
                    apply(); // Jump without animation
                  }
                }, 320);
              },
              { signal }
            );
            next.addEventListener(
              'click',
              (e) => {
                e.preventDefault();
                root._sgIndex = (root._sgIndex ?? 1) + 1;
                const originalCount = root._sgOriginalCount || n;
                
                applyWithState();
                
                // If we're at the cloned first slide, jump to real first slide
                setTimeout(() => {
                  if (root._sgIndex === originalCount + 1) {
                    root._sgIndex = 1;
                    apply(); // Jump without animation
                  }
                }, 320);
              },
              { signal }
            );
          }
          window.addEventListener('resize', applyWithState, { signal });
          applyWithState();
        }
      }

      _reflowCarousels() {
        for (const root of this.querySelectorAll('[data-js-sg-carousel]')) {
          if (root instanceof HTMLElement && typeof root._sgApply === 'function') {
            root._sgApply();
          }
        }
      }

      /**
       * @param {HTMLElement | null} opener
       */
      open(opener) {
        if (this.dataset.state === 'open') {
          return;
        }
        this._lastFocus = opener instanceof HTMLElement ? opener : null;
        this.dataset.state = 'open';
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('component-size-guide-drawer-open');

        const closeBtn = this.querySelector('[data-js-size-guide-close]');
        if (closeBtn instanceof HTMLElement) {
          closeBtn.focus();
        }

        opener?.setAttribute('aria-expanded', 'true');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._initCarousels();
            this._reflowCarousels();
          });
        });
      }

      close() {
        if (this.dataset.state !== 'open') {
          return;
        }
        this.dataset.state = 'closed';
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('component-size-guide-drawer-open');

        const opener =
          this._lastFocus && document.body.contains(this._lastFocus)
            ? this._lastFocus
            : document.querySelector(`[data-size-guide-target="${this.id}"]`);
        opener?.setAttribute('aria-expanded', 'false');
        if (opener instanceof HTMLElement) {
          opener.focus();
        }
        this._lastFocus = null;
      }
    }
  );
}
